import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, NgZone, OnInit, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SearchResult } from '@shared/models/search/search-result';
import { PriceOnCardComponent } from '@shared/shared/components/price-on-card/price-on-card.component';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { CardComponent, CardValuePropositionDirective } from '@shared/shared/modules/card/containers/card.component';
import { CardStyleEnum } from '@shared/shared/modules/card/models/card';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { Angulartics2Segment } from 'angulartics2';
import { MAP_RESULT_LAST_ITEM, RESTAURANT_DETAIL_PAGE } from 'app.config';
import { MapComponent } from '../map/map.component';

interface GeoLocation {
  features: GeoLocationFeature[];
  type: string;
}
interface GeoLocationFeature {
  id: number;
  type: string;
  geometry: { coordinates: number[]; type: string };
  properties: { popupHTML: string };
}

@Component({
  selector: 'ten-map-result-list',
  templateUrl: './map-result-list.component.html',
  styleUrls: ['./map-result-list.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    MapComponent,
    AsyncPipe,
    TranslateModule,
    NgFor,
    CardComponent,
    CardValuePropositionDirective,
    PriceOnCardComponent,
    TranslateModule,
  ],
})
export class MapResultListMapComponent extends BaseComponent implements OnInit {
  @Input() public isNearMeSelected: boolean;
  @Input() public userPosition: { longitude: number; latitude: number };
  @Input() public overlayText?: string;
  @Input() public mapId: string;
  @Input() public noPinsVisibleText?: string;
  @Input() public badgesOnTop? = false;
  @Input() public isRecenterButton = false;
  @Input() public asyncPageLoad = false;
  @Input() public openNewTab = false;

  @Input() public set searchResultsCards(value: SearchResult[]) {
    if (value) {
      this.searchResultAsCards = value.map((data: SearchResult) => {
        return { ...data, cardStyle: CardStyleEnum.horizontal, darkTheme: false };
      });

      this.geoLocations = this.createGeoLocationArray(value);
    }
  }

  @Output() public cardClicked = new EventEmitter<any>();
  @Output() public zoomIn = new EventEmitter<mapboxgl.MapboxGeoJSONFeature>();
  @Output() public zoomOut = new EventEmitter<mapboxgl.MapboxGeoJSONFeature>();
  @Output() public featureClicked = new EventEmitter<mapboxgl.MapboxGeoJSONFeature>();

  @HostListener('window:resize', ['$event'])
  public onResize() {
    this.isMobile = window.innerWidth < 992;
  }

  public activeSearchResultItemId$ = new EventEmitter<string | number | undefined>();

  public isMobile = false;
  private initialLoadDone = false;
  public isLoader = true;
  public searchResultAsCards: SearchResult[];
  public geoLocations: GeoLocation;
  public cardList: SearchResult[] = [];
  public componentData: any;

  constructor(
    private ngZone: NgZone,
    private angulartics2Segment: Angulartics2Segment,
    private translationService: TranslateService,
    siteConfService: SiteConfigService
  ) {
    super(siteConfService, 'header');
  }

  public ngOnInit() {
    this.onResize();
  }

  public cardClick(event: Event, item: SearchResult) {
    if (item.type === 'dining-result') {
      localStorage.setItem(RESTAURANT_DETAIL_PAGE, 'true');
      this.angulartics2Segment.eventTrack('Restaurant card clicked map view', {});
    }
    if (event.type === 'auxclick') {
      localStorage.removeItem(MAP_RESULT_LAST_ITEM);
    } else {
      localStorage.setItem(
        MAP_RESULT_LAST_ITEM,
        JSON.stringify({
          cardId: item.id,
        })
      );
    }

    this.cardClicked.next({ item, isOnLandingPage: false, eventType: event.type });
  }

  public getPinList(pins: number[]) {
    this.ngZone.run(() => {
      this.isLoader = true;
      if (!this.initialLoadDone && pins.length) {
        this.initialLoadDone = true;
      }

      const visibleCards = this.createSearchResultList(this.searchResultAsCards, pins);
      this.cardList = visibleCards?.slice(0, 30);
      this.selectFirstItem();
    });
  }

  public pinClicked(event: mapboxgl.MapboxGeoJSONFeature) {
    this.scrollResultList(event.id.toString());
    this.featureClicked.emit(event);
  }

  public showActivePinWithoutZoom(id?: string | number) {
    if (id) {
      this.activeSearchResultItemId$.emit(id);
    }
  }

  private createGeoLocationArray(value: Array<SearchResult>): GeoLocation {
    if (value) {
      const geoJsonResults = value.map((data: SearchResult) => {
        if (data.location && +data.location.longitude !== 0) {
          return {
            id: +data.uuid || +data.id || Date.now(),
            geometry: {
              coordinates: [+data.location.longitude, +data.location.latitude],
              type: 'Point',
            },
            type: 'Feature',
            properties: {
              popupHTML: data.mapPopupHTML,
            },
          };
        }
      });

      if (this.isNearMeSelected && this.userPosition) {
        geoJsonResults.push({
          id: Date.now(),
          geometry: {
            coordinates: [this.userPosition.longitude, this.userPosition.latitude],
            type: 'Point',
          },
          type: 'Feature',
          properties: {
            popupHTML: `<div class='popup-header'>${this.translationService.instant('MapView:YouAreHere')}</div>`,
          },
        });
      }

      return {
        features: geoJsonResults.filter((item) => item),
        type: 'FeatureCollection',
      };
    }
  }

  private createSearchResultList(list: SearchResult[], pins: number[]): SearchResult[] {
    if (!list || !pins) {
      return [];
    }

    return list.filter((x: SearchResult) => {
      return pins.some((pin: number) => pin.toString() === `${x.uuid || x.id}`);
    });
  }

  private scrollToCardElement(cardId: string, cardElement: HTMLElement) {
    if (!cardElement) {
      this.scrollResultList(cardId);
    } else {
      const headerWrap = document.getElementsByClassName('global-nav__wrap')[0] as HTMLElement;

      const cardElementInner = cardElement.getElementsByClassName('card')[0] as HTMLElement;

      const html = document.getElementsByTagName('html')[0];
      const cardElementBounds = cardElement.getBoundingClientRect();
      const cardElementParent = cardElement.parentElement;
      const positionVertical = cardElementBounds.top + window.scrollY - headerWrap.clientHeight - 15;
      const positionHorizontal = cardElement.offsetLeft - 15;

      cardElementInner.focus({ preventScroll: true });

      if (this.isMobile) {
        const positionVerticalMobile = cardElementBounds.bottom + window.scrollY - window.innerHeight;

        cardElementParent.scrollTo({ top: 0, left: positionHorizontal, behavior: 'smooth' });
        html.scrollTo({ top: positionVerticalMobile, left: 0, behavior: 'smooth' });
      } else {
        html.scrollTo({ top: positionVertical, left: 0, behavior: 'smooth' });
      }
    }
  }

  private scrollResultList(cardId: string) {
    setTimeout(() => {
      const cardElement = document.getElementById('card_element_' + cardId);
      this.scrollToCardElement(cardId, cardElement);
    });
  }

  private selectFirstItem() {
    const lastItem = JSON.parse(localStorage.getItem(MAP_RESULT_LAST_ITEM));
    const cardId = lastItem?.cardId || this.cardList[0]?.uuid?.toString();

    if (cardId) {
      this.activeSearchResultItemId$.emit(cardId);
      this.scrollResultList(cardId);
    } else if (this.initialLoadDone && !this.cardList.length) {
      this.isLoader = false;
    }

    localStorage.removeItem(MAP_RESULT_LAST_ITEM);
  }
}
